import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'

import { FxIcon } from '@/components/fx'

/**
 * The 640px dialog the artboards draw: `radius-xl`, `shadow-overlay`, no
 * border, a close button in the corner. Radix supplies the focus trap,
 * Escape, the scroll lock and the `aria-labelledby` wiring.
 *
 * This is the third local copy of that shell (`components/share/
 * ShareDialogShell` and `pages/projekte/ProjektDialog` are the other two) and
 * it exists for one reason those two do not cover: `dismissable={false}`.
 * While a freshly created API key is on screen, Escape and a click on the
 * backdrop must not close the dialog — a stray click would take a secret with
 * it that nobody, including us, can show again. Neither existing copy passes
 * anything through to `Dialog.Content`, and `components/fx` is not this
 * wave's to extend, so the duplication is reported rather than smuggled in:
 * all three belong in `components/fx` as one `FxDialog`.
 */
export function SettingsDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissable = true,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  dismissable?: boolean
}) {
  const block = (event: Event) => {
    if (!dismissable) event.preventDefault()
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/55" />
        <Dialog.Content
          onEscapeKeyDown={block}
          onInteractOutside={block}
          onPointerDownOutside={block}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[min(640px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-xl bg-surface p-6 text-body text-text shadow-overlay sm:p-10"
        >
          <div className="flex items-start gap-4">
            <Dialog.Title className="m-0 flex-1 text-dialog-title">{title}</Dialog.Title>
            {dismissable && (
              <Dialog.Close
                className="-mr-2 -mt-2 flex h-11 w-11 flex-none items-center justify-center rounded-sm text-text"
                aria-label="Dialog schließen"
              >
                <FxIcon name="close" size={28} />
              </Dialog.Close>
            )}
          </div>
          {description && (
            <Dialog.Description className="m-0 text-body text-text-muted">
              {description}
            </Dialog.Description>
          )}
          {children}
          {footer && <div className="flex flex-wrap items-center justify-end gap-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
